import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Divider,
    Icon,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {ChangeEvent, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import {AccessProjectType, FolderDto} from "../../../api/psc";
import {MultiSelect} from "../../../components/exports";
import {DialogContext} from "../../../contexts/DialogueContext";
import {RootState} from "../../../slices/Store";
import SelectFolderItem from "../../project-detail/Shared/SelectFolderItem";
import {
    reloadPromotedProject,
    setPromotedProjectFilter,
} from "../slice/PromotedProjectSlice";
import ProjectAccessCountAvailabilityBox from "./ProjectAccessCountAvailabilityBox";
import {useFetchBusinessLines} from "../api-hooks/projectsQuery";

// updated
export default function PromotedProjectFilter() {
    const dispatch = useDispatch();

    const {businessLines, isLoadingBusinessLines} = useFetchBusinessLines();

    const promotedProjectState = useSelector(
        (state: RootState) => state.promoted
    );
    const sharedState = useSelector((state: RootState) => state.shared);

    const {closeDialog} = useContext(DialogContext);

    const applyFilter = (e) => {
        e.preventDefault();
        dispatch(reloadPromotedProject());
    };

    const handleRequirementChange = (values: number[]) => {
        const filter = {
            ...promotedProjectState.filter,
            businessLineIds: values,
        };
        dispatch(setPromotedProjectFilter(filter));
    };

    const handlePhaseChange = (values: number[]) => {
        const filter = {...promotedProjectState.filter, phaseIds: values};
        dispatch(setPromotedProjectFilter(filter));
    };

    const handleCiyChange = (values: number[]) => {
        const filter = {
            ...promotedProjectState.filter,
            cityIds: values,
            regionIds: [],
            subRegionIds: [],
        };
        dispatch(setPromotedProjectFilter(filter));
    };

    const handleRegionChange = (values: number[]) => {
        const filter = {
            ...promotedProjectState.filter,
            regionIds: values,
            subRegionIds: [],
        };
        dispatch(setPromotedProjectFilter(filter));
    };

    const handleSubRegionChange = (values: number[]) => {
        const filter = {...promotedProjectState.filter, subRegionIds: values};
        dispatch(setPromotedProjectFilter(filter));
    };

    const handleTermChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const filter = {
            ...promotedProjectState.filter,
            term: elem.target.value,
        };
        dispatch(setPromotedProjectFilter(filter));
    };

    const handleFolderToggle = (folder: FolderDto) => {
        const updatedFolderIds = [...promotedProjectState.filter.folderIds];
        const isFolderAdded = updatedFolderIds.includes(folder.id);

        if (isFolderAdded) {
            updatedFolderIds.splice(updatedFolderIds.indexOf(folder.id), 1);
        } else {
            updatedFolderIds.push(folder.id);
        }

        const filter = {
            ...promotedProjectState.filter,
            folderIds: updatedFolderIds,
        };

        dispatch(setPromotedProjectFilter(filter));
    };

    const availableCities = sharedState.baseInfo.cities;

    const availablePhases = sharedState.baseInfo.phaseList;

    const availableRegions = availableCities
        .filter(
            (c) =>
                availableCities.length === 1 ||
                promotedProjectState.filter.cityIds.includes(c.id)
        )
        .map((c) => c.regions)
        .reduce((r1, r2) => r1?.concat(r2) ?? [], []);

    const availableSubRegions = availableRegions
        .filter(
            (r) =>
                promotedProjectState.filter.regionIds.length === 0 ||
                promotedProjectState.filter.regionIds.includes(r.id)
        )
        .map((r) => r.subregions)
        .reduce((r1, r2) => r1?.concat(r2) ?? [], []);

    const promotedAccess = sharedState.accessList.find(
        (a) => a.projectType === AccessProjectType.PROMOTED
    );

    return (
        <form onSubmit={applyFilter}>
            <Stack spacing={2} sx={{p: 2, minWidth: 330}}>
                <Stack spacing={1} direction="row" alignItems="end">
                    <Typography sx={{flexGrow: 1}}>فیلتر پروژه ها</Typography>

                    <LoadingButton
                        type="submit"
                        endIcon={<Icon>filter_alt</Icon>}
                        loading={promotedProjectState.isLoading}
                        variant="contained"
                    >
                        اعمال فیلتر
                    </LoadingButton>

                    <IconButton
                        onClick={closeDialog}
                        sx={{display: {md: "none", sm: "block"}}}
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </Stack>

                <Divider/>

                {promotedAccess && promotedAccess?.viewCounter && (
                    <ProjectAccessCountAvailabilityBox
                        access={promotedAccess}
                    />
                )}

                {!promotedAccess && (
                    <Alert severity="error">
                        شما دسترسی به پروژه های بازاریابی ندارید!
                    </Alert>
                )}

                <Divider>
                    <Typography color="text.secondary">
                        فیلتر های اصلی
                    </Typography>
                </Divider>

                {!isLoadingBusinessLines &&
                    <MultiSelect
                        label="دسته بندی نیازمندی ها"
                        values={businessLines}
                        hasSearch={true}
                        selectedIds={
                            promotedProjectState.filter.businessLineIds
                        }
                        onValueChange={handleRequirementChange}
                    />
                }

                <MultiSelect
                    label="مرحله"
                    values={availablePhases}
                    selectedIds={promotedProjectState.filter.phaseIds}
                    onValueChange={handlePhaseChange}
                />

                {availableCities.length > 1 && (
                    <MultiSelect
                        label="شهر"
                        values={availableCities}
                        selectedIds={promotedProjectState.filter.cityIds}
                        onValueChange={handleCiyChange}
                    />
                )}

                {promotedProjectState.filter.cityIds.length <= 1 &&
                    availableRegions.length > 0 && (
                        <MultiSelect
                            label="منطقه"
                            values={availableRegions}
                            selectedIds={promotedProjectState.filter.regionIds}
                            onValueChange={handleRegionChange}
                        />
                    )}

                {promotedProjectState.filter.regionIds.length > 0 &&
                    availableSubRegions.length > 0 && (
                        <MultiSelect
                            label="محله"
                            values={availableSubRegions}
                            selectedIds={
                                promotedProjectState.filter.subRegionIds
                            }
                            onValueChange={handleSubRegionChange}
                        />
                    )}

                <TextField
                    fullWidth
                    value={promotedProjectState.filter.term}
                    onChange={handleTermChange}
                    label="جستجو"
                    placeholder={"جستجو یا شناسه، موبایل، آدرس یا نام سازنده"}
                    variant="outlined"
                />

                {sharedState.folders.length > 0 && (
                    <Stack spacing={2}>
                        <Divider>
                            <Typography color="text.secondary">
                                پوشه ها
                            </Typography>
                        </Divider>

                        {sharedState.folders.map((f) => (
                            <SelectFolderItem
                                key={f.id}
                                folder={f}
                                onToggle={handleFolderToggle}
                                checked={promotedProjectState.filter.folderIds.includes(
                                    f.id
                                )}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </form>
    );
}
